import Cookies from 'js-cookie';
import { AxiosError } from 'axios';

import { setTokens } from '~/domains/auth/utils/setTokens';
import { removeTokens } from '~/domains/auth/utils/removeTokens';
import log from '~/common/utils/log';
import { Routes } from '~/domains/common/constants/routes';
import { reportEvent } from '~/domains/analytics';

import { identityInstance } from './identity-instance';
import type { APIAuthRefreshTokenResponse } from './types';

let isRedirecting = false;
let refreshPromise: Promise<APIAuthRefreshTokenResponse['data']> | null = null;

export const fetchRefreshToken = async (
  shouldRedirect = true,
  onErrorRedirectTo = Routes.signIn({
    brand: new URLSearchParams(window.location.search).get('brand') || undefined,
  })
): Promise<APIAuthRefreshTokenResponse['data']> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const refreshToken = Cookies.get('refreshToken');
      const currentAccessToken = Cookies.get('accessToken');

      if (!refreshToken) {
        throw Error('No refresh token');
      }

      if (!currentAccessToken) {
        throw Error('No access token');
      }

      // Helper function to wait for specified seconds
      const waitForRetryAfter = (seconds: number): Promise<void> =>
        new Promise((resolve) => {
          setTimeout(resolve, seconds * 1000);
        });

      // Recursive function to handle retries for 429 errors with 30-second timeout
      const startTime = Date.now();
      const timeoutMs = 30 * 1000; // 30 seconds

      const attemptRefresh = async (): Promise<APIAuthRefreshTokenResponse['data']> => {
        // Check if we've exceeded the 30-second timeout
        if (Date.now() - startTime > timeoutMs) {
          throw new Error('Failed to refresh token: exceeded 30-second retry timeout due to rate limiting');
        }

        try {
          const response: APIAuthRefreshTokenResponse = await identityInstance.post(
            '/universal-auth/refresh',
            {
              refreshToken,
              accessToken: currentAccessToken,
            }
          );

          // Success - return the token data
          const tokenData = response.data;
          const { accessToken } = tokenData;
          setTokens({ accessToken });
          return tokenData;

        } catch (error) {
          // Check if it's a 429 error (Cognito rate limiting)
          if (error instanceof AxiosError && error.response?.status === 429) {
            const retryAfterHeader = error.response.headers['retry-after'];
            const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 2; // Default to 2 seconds if header is missing

            // Check if waiting for the retry delay would exceed our timeout
            const remainingTime = timeoutMs - (Date.now() - startTime);
            const retryDelayMs = retryAfterSeconds * 1000;

            if (retryDelayMs >= remainingTime) {
              throw new Error(`Failed to refresh token: retry delay (${retryAfterSeconds}s) would exceed remaining timeout (${Math.floor(remainingTime / 1000)}s)`);
            }

            log(`Received 429 status, retrying after ${retryAfterSeconds} seconds (${Math.floor(remainingTime / 1000)}s remaining)`);

            await waitForRetryAfter(retryAfterSeconds);
            return attemptRefresh();
          }

          // For any other error, throw it to be handled by the outer catch block
          throw error;
        }
      };

      return await attemptRefresh();

    } catch (error) {
      reportEvent('User > Fetch RefreshToken Error', error);

      removeTokens();

      if (shouldRedirect && !isRedirecting) {
        isRedirecting = true;
        const fullUrl = `${window.location.origin}${onErrorRedirectTo}`;
        log(`Redirecting to ${fullUrl} because of a fetchRefreshToken error.`, error);
        window.location.href = fullUrl;
      }
      throw error;
    } finally {
      setTimeout(() => {
        refreshPromise = null;
      }, 1000);
    }
  })();

  return refreshPromise;
};
